import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swords, Shield, Zap, Star, Copy, ChevronRight, X, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import type { Card } from '../../types/card';

interface CombatStats {
  total_combats: number;
  victories: number;
  defeats: number;
  win_ratio: number;
  current_streak: number;
}

const DEFAULT_STATS: CombatStats = {
  total_combats: 0,
  victories: 0,
  defeats: 0,
  win_ratio: 0,
  current_streak: 0
};

export default function Combat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<CombatStats>(DEFAULT_STATS);
  const [cards, setCards] = useState<Card[]>([]);
  const [invitationCode, setInvitationCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First ensure combat stats exist
        const { error: rpcError } = await supabase.rpc('ensure_combat_stats', {
          user_id: user.id
        });

        if (rpcError) {
          console.error('Error ensuring combat stats:', rpcError);
        }

        // Then fetch the stats
        const { data: statsData, error: statsError } = await supabase
          .from('combat_stats')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (statsError && !statsError.message.includes('JSON object requested, multiple (or no) rows returned')) {
          console.error('Error fetching combat stats:', statsError);
        }

        setStats(statsData || DEFAULT_STATS);

        // Fetch user's cards
        const { data: cardsData, error: cardsError } = await supabase
          .from('cards')
          .select('*')
          .eq('owner_id', user.id);

        if (cardsError) throw cardsError;
        setCards(cardsData || []);

      } catch (err) {
        console.error('Error fetching combat data:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const getRandomCards = (count: number) => {
    if (cards.length < count) {
      throw new Error(`Vous devez avoir au moins ${count} cartes pour combattre`);
    }
    
    // Create a copy of the cards array to avoid modifying the original
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  const generateInvitationCode = async () => {
    try {
      if (cards.length < 3) {
        throw new Error('Vous devez avoir au moins 3 cartes pour combattre');
      }

      // Randomly select 3 cards
      const selectedCards = getRandomCards(3);
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const { error: sessionError } = await supabase
        .from('combat_sessions')
        .insert({
          invitation_code: code,
          host_id: user?.id,
          status: 'WAITING',
          host_cards: selectedCards
        });

      if (sessionError) throw sessionError;

      setInvitationCode(code);
    } catch (err) {
      console.error('Error creating combat session:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la création du combat');
    }
  };

  const joinCombat = async () => {
    if (!joinCode) {
      setError('Veuillez entrer un code d\'invitation');
      return;
    }

    try {
      // Get the session first
      const { data: session, error: sessionError } = await supabase
        .from('combat_sessions')
        .select('*')
        .eq('invitation_code', joinCode.toUpperCase())
        .eq('status', 'WAITING')
        .single();

      if (sessionError) throw new Error('Code d\'invitation invalide');
      if (session.host_id === user?.id) throw new Error('Vous ne pouvez pas rejoindre votre propre combat');

      // Randomly select 3 cards for the guest
      const selectedCards = getRandomCards(3);

      // Update the session with guest cards
      const { error: updateError } = await supabase
        .from('combat_sessions')
        .update({
          guest_id: user?.id,
          guest_cards: selectedCards,
          status: 'IN_PROGRESS'
        })
        .eq('id', session.id);

      if (updateError) throw updateError;

      // Navigate to the arena
      navigate(`/combat/arena/${session.id}`);
    } catch (err) {
      console.error('Error joining combat:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la connexion au combat');
    }
  };

  const calculateTotalScore = (card: Card) => {
    return card.attaque + card.defense + card.special;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Combat</h1>
        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Combats</p>
              <p className="text-2xl font-bold">{stats.total_combats}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Victoires</p>
              <p className="text-2xl font-bold text-green-600">{stats.victories}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Défaites</p>
              <p className="text-2xl font-bold text-red-600">{stats.defeats}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Ratio V/D</p>
              <p className="text-2xl font-bold">{stats.win_ratio}%</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Série</p>
              <p className="text-2xl font-bold">{stats.current_streak}</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <X className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Créer un combat */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Créer un combat</h2>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              3 cartes seront sélectionnées aléatoirement parmi votre collection pour le combat.
              Vous avez actuellement {cards.length} carte{cards.length !== 1 ? 's' : ''}.
            </p>
            <button
              onClick={generateInvitationCode}
              disabled={cards.length < 3}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Générer un code d'invitation
            </button>
          </div>
          {invitationCode && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Code d'invitation :</p>
              <div className="flex items-center space-x-2">
                <code className="px-4 py-2 bg-white rounded border text-lg font-mono">
                  {invitationCode}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(invitationCode)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Rejoindre un combat */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Rejoindre un combat</h2>
          <div>
            <p className="text-sm text-gray-600 mb-4">
              3 cartes seront sélectionnées aléatoirement parmi votre collection pour le combat.
            </p>
            <label htmlFor="joinCode" className="block text-sm font-medium text-gray-700">
              Code d'invitation
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="joinCode"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Entrez le code à 6 caractères"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase"
                maxLength={6}
              />
            </div>
            <button
              onClick={joinCombat}
              disabled={!joinCode || cards.length < 3}
              className="w-full mt-4 inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ENTRER DANS L'ARÈNE
              <ChevronRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}